import type { InjectionKey } from 'vue';
import type { BlockDefinition, BlockSerialized } from '#layers/block-engine/types/block';
import { inject, isRef, provide, toValue } from 'vue';
import { useBlockContext } from '#layers/block-engine/utils/block/api/context';

export const blockInjectionKey = Symbol('monospace:blockInjection') as InjectionKey<Record<string, any>>;

export function setupBlockInjection(block: BlockDefinition | BlockSerialized) {
	const parent = inject(blockInjectionKey, {});

	provide(blockInjectionKey, {
		...parent,
		[block.id]: {},
	});
}

export function provideBlockInjection(injection: Record<string, any>) {
	const { getBlockDefinition } = useBlockContext();
	const key = getBlockDefinition().id;
	const data = inject(blockInjectionKey, {});

	Object.assign(data[key], injection);
}

export function useBlockInjection() {
	const injection = inject(blockInjectionKey, {});

	const unwrappedInjection = createUnwrapProxy(injection);

	return {
		injection,
		unwrappedInjection,
	};
}

/**
 * Support reading/writing to refs directly without needing to use `.value`.
 */
export function createUnwrapProxy(target: any) {
	const cache = new WeakMap<any, any>();

	return new Proxy(target, {
		get(target, prop) {
			const value = target[prop as string];

			if (isRef(value)) {
				return toValue(value);
			}
			else if (typeof value === 'object') {
				if (cache.has(value)) {
					return cache.get(value);
				}

				const proxyValue = createUnwrapProxy(value);
				cache.set(value, proxyValue);
				return proxyValue;
			}
			else {
				return Reflect.get(target, prop);
			}
		},
		set(target, prop, value) {
			if (isRef(target[prop as string])) {
				return Reflect.set(target[prop as string], 'value', value);
			}

			return Reflect.set(target, prop, value);
		},
	});
}
